import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { useBiometricAuth } from '../hooks/useBiometricAuth';
import { usePushNotifications } from '../hooks/usePushNotifications';
import PageHeader from '../components/PageHeader';
import {
  adminApi,
  impersonationApi,
  type ImpersonationAssociate,
  type ImpersonationStatus,
} from '../services/api';
import { getResetCounts, resetSession, type ResetCounts } from '../utils/resetSession';
import { hardResetLocalState } from '../utils/hardReset';
import { getEffectiveAssociateId } from '../utils/associate';
import AvailabilitySection from '../components/AvailabilitySection';
import ContactSection from '../components/ContactSection';

// Get base URL from environment or default
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export default function Profile() {
  const { user, logout, token } = useAuth();
  const [isLoadingSSO, setIsLoadingSSO] = useState(false);
  const { isStandalone, isIOS, promptInstall, resetDismissal } = useInstallPrompt();
  const [installMessage, setInstallMessage] = useState<string | null>(null);
  const {
    isSupported: biometricSupported,
    isEnabled: biometricEnabled,
    isAuthenticating,
    error: biometricError,
    enableBiometric,
    disableBiometric,
    checkBiometricAvailability,
  } = useBiometricAuth();

  const {
    isSupported: notificationsSupported,
    permission: notificationPermission,
    isSubscribed: notificationsEnabled,
    isLoading: notificationsLoading,
    error: notificationError,
    subscribe: subscribeNotifications,
    unsubscribe: unsubscribeNotifications,
  } = usePushNotifications();

  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricMessage, setBiometricMessage] = useState<string | null>(null);
  const [notificationMessage, setNotificationMessage] = useState<string | null>(null);

  // Impersonation state
  const [canImpersonate, setCanImpersonate] = useState(false);
  const [currentImpersonation, setCurrentImpersonation] = useState<ImpersonationStatus | null>(null);
  const [impersonationAssociates, setImpersonationAssociates] = useState<ImpersonationAssociate[]>([]);
  const [showImpersonationList, setShowImpersonationList] = useState(false);
  const [impersonationLoading, setImpersonationLoading] = useState(false);
  const [impersonationError, setImpersonationError] = useState<string | null>(null);
  // Searchable picker (#1891). Default list only shows the activity-top
  // 50 reps, so admins couldn't find anyone outside that bucket — the
  // text input here debounces and re-queries the server's q param so we
  // can find anyone in the active associate table.
  const [impersonationSearch, setImpersonationSearch] = useState('');
  const [impersonationSearching, setImpersonationSearching] = useState(false);

  // Reset Session state (#1873 follow-up — admin recovery tool)
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetCounts, setResetCounts] = useState<ResetCounts | null>(null);
  const [resetPhase, setResetPhase] = useState<'idle' | 'loading' | 'syncing' | 'done' | 'partial' | 'error'>('idle');
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetReportId, setResetReportId] = useState<number | null>(null);

  // Hard Reset Day state (#1964 — admin destructive reset). Two-part
  // server+client void for testers/admins to escape stuck states that
  // the offline-first hydrate would otherwise reconstitute.
  const [showHardResetModal, setShowHardResetModal] = useState(false);
  const [hardResetForceFullDay, setHardResetForceFullDay] = useState(false);
  const [hardResetPhase, setHardResetPhase] = useState<'idle' | 'running' | 'error'>('idle');
  const [hardResetError, setHardResetError] = useState<string | null>(null);
  const [hardResetSummary, setHardResetSummary] = useState<string | null>(null);

  // Handle both camelCase and snake_case from API
  const displayName = user?.firstName || user?.first_name || 'Field Rep';
  const lastName = user?.lastName || user?.last_name || '';
  const email = user?.email || '';

  // Get stored permissions
  const permissions = (() => {
    try {
      const stored = localStorage.getItem('permissions');
      return stored ? JSON.parse(stored) as string[] : [];
    } catch {
      return [];
    }
  })();

  // Calculate offline storage metrics
  const storageMetrics = (() => {
    try {
      const keys = Object.keys(localStorage);
      let totalBytes = 0;
      const items: { key: string; size: number; label: string }[] = [];

      keys.forEach(key => {
        const value = localStorage.getItem(key) || '';
        const size = value.length * 2; // UTF-16 characters = 2 bytes each
        totalBytes += size;

        // Create human-readable labels
        const labels: Record<string, string> = {
          'accessToken': 'Auth Token',
          'refreshToken': 'Refresh Token',
          'associate': 'User Profile',
          'permissions': 'Permissions',
          'rdm_offline_queue': 'Offline Queue',
          'pwa-install-dismissed': 'App Settings',
        };

        if (size > 0) {
          items.push({
            key,
            size,
            label: labels[key] || key,
          });
        }
      });

      return { totalBytes, items: items.sort((a, b) => b.size - a.size) };
    } catch {
      return { totalBytes: 0, items: [] };
    }
  })();

  // State for expandable sections
  const [showPermissions, setShowPermissions] = useState(false);
  const [showStorageDetails, setShowStorageDetails] = useState(false);

  // Format bytes to human readable
  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Check biometric availability on mount
  useEffect(() => {
    async function checkBiometric() {
      if (biometricSupported) {
        const available = await checkBiometricAvailability();
        setBiometricAvailable(available);
      }
    }
    checkBiometric();
  }, [biometricSupported, checkBiometricAvailability]);

  // Impersonation gate on mount — NO network (#2116 follow-up, diag #52).
  // This used to fire its own /api/auth/impersonate/list/ on every Profile
  // mount (1.5-2.9s each on field links) on top of useImpersonation's
  // verify; the verify is now cached module-level and keeps localStorage in
  // sync, so Profile reads the gate from is_superuser + localStorage and
  // the picker-open effect below fetches the associates list on demand.
  useEffect(() => {
    if (!user?.is_superuser) {
      setCanImpersonate(false);
      return;
    }
    setCanImpersonate(true);
    try {
      const stored = localStorage.getItem('impersonating');
      setCurrentImpersonation(stored ? JSON.parse(stored) : null);
    } catch {
      setCurrentImpersonation(null);
    }
  }, [user?.is_superuser]);

  // Debounced search against impersonate/list?q=... (#1891). 250ms keeps
  // keystrokes responsive without spamming the server. AbortController
  // cancels in-flight queries when the input changes so we never render
  // stale results from an earlier search.
  useEffect(() => {
    if (!canImpersonate || !showImpersonationList) return;
    const term = impersonationSearch.trim();
    const ac = new AbortController();
    const t = window.setTimeout(async () => {
      setImpersonationSearching(true);
      try {
        const response = await impersonationApi.getList(term || undefined);
        if (!ac.signal.aborted) {
          setImpersonationAssociates(response.associates);
        }
      } catch {
        if (!ac.signal.aborted) setImpersonationAssociates([]);
      } finally {
        if (!ac.signal.aborted) setImpersonationSearching(false);
      }
    }, 250);
    return () => {
      ac.abort();
      window.clearTimeout(t);
    };
  }, [impersonationSearch, canImpersonate, showImpersonationList]);

  // Reset Session — open modal + load counts (#1873 follow-up)
  const handleOpenResetModal = async () => {
    setShowResetModal(true);
    setResetError(null);
    setResetReportId(null);
    setResetPhase('loading');
    try {
      const counts = await getResetCounts();
      setResetCounts(counts);
      setResetPhase('idle');
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Could not load queue counts');
      setResetPhase('error');
    }
  };

  const handleConfirmReset = async (forceClearOnSyncFailure: boolean) => {
    setResetPhase('syncing');
    setResetError(null);
    try {
      const result = await resetSession({ forceClearOnSyncFailure });
      setResetReportId(result.diagnosticsReportId);
      const hadFailure =
        result.syncedVisitEntries.failed > 0
        || result.syncedExpenses.failed > 0
        || result.errors.length > 0;
      if (hadFailure && !forceClearOnSyncFailure) {
        setResetError(`Sync had failures: ${result.errors.join('; ') || 'see queue counts'}. Local state was NOT cleared.`);
        setResetPhase('partial');
        // Refresh counts so the modal shows current state
        try {
          setResetCounts(await getResetCounts());
        } catch {
          // ignore
        }
        return;
      }
      setResetPhase('done');
      // Reload to fetch fresh state from the server
      window.setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Reset failed');
      setResetPhase('error');
    }
  };

  // Hard Reset Day (#1964) — admin-only two-part destructive reset.
  // Step 1: server voids the impersonated rep's day so the offline-first
  // hydrate cannot reconstitute stuck state.
  // Step 2: local nuke (every IDB DB, localStorage, sessionStorage,
  // caches API, service worker unregister).
  // Step 3: force re-login.
  const handleHardResetDay = async () => {
    setHardResetPhase('running');
    setHardResetError(null);
    setHardResetSummary(null);
    try {
      // Resolve the target associate. Prefer the impersonated rep so an admin
      // testing as Rob doesn't accidentally void their own day; otherwise fall
      // back to the logged-in user's OWN associate. Impersonation is RESPECTED
      // when active but never REQUIRED — getEffectiveAssociateId reads the same
      // stored `associate` payload every other write-path uses (the User object
      // from /api/auth/user/ carries no associate_id).
      const targetAssociateId = currentImpersonation?.associateid ?? getEffectiveAssociateId();
      if (!targetAssociateId) {
        throw new Error(
          'No associate context found for your account. Try logging out and back in.'
        );
      }

      // 1. Server void.
      const serverResp = await adminApi.resetRepDay(targetAssociateId, {
        forceFullDay: hardResetForceFullDay,
      });
      if (serverResp.status !== 'success') {
        throw new Error(serverResp.message || 'Server reset failed.');
      }

      // 2. Local nuke. After this point the React tree may render
      // inconsistently because state stores are gone — that's fine, we
      // navigate away immediately.
      const localResult = await hardResetLocalState();

      // 3. Drop a brief breadcrumb the admin sees on the login screen
      // (via the next render). Stored in a global var because every
      // storage layer we know about was just nuked.
      const summary = `Voided ${serverResp.voided ?? 0} CallLog(s) on ${serverResp.date}; cleared ${localResult.indexedDBs.length} IDB(s), ${localResult.caches.length} cache(s), ${localResult.serviceWorkers} SW(s).`;
      setHardResetSummary(summary);

      // 4. Hard reload to /login. Use replace so back-button can't
      // re-enter the in-progress reset modal.
      window.location.replace('/login');
    } catch (err) {
      setHardResetError(err instanceof Error ? err.message : 'Hard reset failed');
      setHardResetPhase('error');
    }
  };

  // Handle impersonation selection
  const handleImpersonate = async (associate: ImpersonationAssociate) => {
    setImpersonationLoading(true);
    setImpersonationError(null);
    try {
      const response = await impersonationApi.setImpersonation(associate.associateid);
      if (response.status === 'success' && response.impersonating) {
        setCurrentImpersonation(response.impersonating);
        setShowImpersonationList(false);
        // Store impersonation info for global header banner
        localStorage.setItem('impersonating', JSON.stringify(response.impersonating));
        // Refresh the cached associate to the now-impersonated rep so the PWA
        // sends the correct associate id in API payloads (#1499).
        if (response.associate) {
          localStorage.setItem('associate', JSON.stringify(response.associate));
        }
        // Reload the page to refresh all data with new impersonation
        window.location.reload();
      }
    } catch (error) {
      setImpersonationError('Failed to set impersonation');
    } finally {
      setImpersonationLoading(false);
    }
  };

  // Handle biometric toggle
  const handleBiometricToggle = async () => {
    setBiometricMessage(null);
    if (biometricEnabled) {
      disableBiometric();
      setBiometricMessage('Biometric login disabled');
    } else {
      const userId = user?.email || 'user';
      const success = await enableBiometric(userId);
      if (success) {
        setBiometricMessage('Biometric login enabled successfully!');
      }
    }
  };

  // Handle notifications toggle
  const handleNotificationsToggle = async () => {
    setNotificationMessage(null);
    if (notificationsEnabled) {
      const success = await unsubscribeNotifications();
      if (success) {
        setNotificationMessage('Notifications disabled');
      }
    } else {
      const success = await subscribeNotifications();
      if (success) {
        setNotificationMessage('Notifications enabled successfully!');
      }
    }
  };

  // Handle install app prompt
  const handleInstallApp = async () => {
    setInstallMessage(null);
    if (isIOS) {
      // For iOS, reset dismissal which will show instructions
      resetDismissal();
    } else {
      // For Android/desktop, try to prompt install
      const installed = await promptInstall();
      if (installed) {
        setInstallMessage('App installed successfully!');
      }
    }
  };

  // Handle SSO redirect to legacy app
  const handleOpenLegacyApp = async () => {
    if (isLoadingSSO) return;

    setIsLoadingSSO(true);
    try {
      const response = await fetch(`${BASE_URL}/api/auth/sso/generate-frontend/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          return_url: '/projects/downloadDocument',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate SSO token');
      }

      const data = await response.json();
      window.location.href = data.redirect_url;
    } catch (error) {
      console.error('SSO error:', error);
      window.open('http://localhost:5173', '_blank');
    } finally {
      setIsLoadingSSO(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <PageHeader title="Profile" subtitle="Account & Settings" />

      <main className="p-4">
        {/* User Info Card */}
        <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <span className="font-heading text-2xl font-bold">
                {displayName.charAt(0)}{lastName.charAt(0)}
              </span>
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-lg font-semibold text-slate-800">
                {displayName} {lastName}
              </h2>
              <p className="text-sm text-slate-500">{email}</p>
              {/* Admin badges */}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {canImpersonate && (
                  <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                    Super Admin
                  </span>
                )}
                {permissions.includes('ability_to_select_rep_for_timesheet') && (
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                    Manager
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* #2986 — contact & address (geocode + timezone on save) */}
        <div className="mb-4">
          <ContactSection />
        </div>

        {/* #2975 — availability & PTO requests */}
        <div className="mb-4">
          <AvailabilitySection />
        </div>

        {/* Account Info - Permissions & Storage */}
        <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
          <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
            Account Info
          </h3>

          {/* Permissions */}
          <button
            onClick={() => setShowPermissions(!showPermissions)}
            className="flex w-full items-center justify-between rounded-lg bg-slate-50 p-3 transition-colors hover:bg-slate-100"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100">
                <svg className="h-4 w-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="text-left">
                <span className="text-sm font-medium text-slate-800">Permissions</span>
                <p className="text-xs text-slate-500">{permissions.length} active permissions</p>
              </div>
            </div>
            <svg
              className={`h-4 w-4 text-slate-400 transition-transform ${showPermissions ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showPermissions && (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {permissions.map((perm) => (
                <div key={perm} className="flex items-center gap-2 py-1 text-xs text-slate-600">
                  <svg className="h-3 w-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {perm.replace(/_/g, ' ').replace(/can /i, '')}
                </div>
              ))}
            </div>
          )}

          {/* Offline Storage */}
          <button
            onClick={() => setShowStorageDetails(!showStorageDetails)}
            className="mt-2 flex w-full items-center justify-between rounded-lg bg-slate-50 p-3 transition-colors hover:bg-slate-100"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100">
                <svg className="h-4 w-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              </div>
              <div className="text-left">
                <span className="text-sm font-medium text-slate-800">Offline Storage</span>
                <p className="text-xs text-slate-500">{formatBytes(storageMetrics.totalBytes)} cached</p>
              </div>
            </div>
            <svg
              className={`h-4 w-4 text-slate-400 transition-transform ${showStorageDetails ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showStorageDetails && (
            <div className="mt-2 rounded-lg border border-slate-200 p-2">
              {storageMetrics.items.map((item) => (
                <div key={item.key} className="flex items-center justify-between py-1 text-xs">
                  <span className="text-slate-600">{item.label}</span>
                  <span className="font-mono text-slate-400">{formatBytes(item.size)}</span>
                </div>
              ))}
              {storageMetrics.items.length === 0 && (
                <p className="py-2 text-center text-xs text-slate-400">No offline data stored</p>
              )}
            </div>
          )}
        </div>

        {/* Help — surfaced at the top of More so it's easy to find */}
        <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
          <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
            Help
          </h3>
          <Link
            to="/help"
            className="flex w-full items-center justify-between rounded-lg bg-indigo-50 p-4 transition-colors hover:bg-indigo-100"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100">
                <svg className="h-5 w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-left">
                <span className="font-medium text-slate-800">Help Guide</span>
                <p className="text-xs text-slate-500">How-to guides and troubleshooting</p>
              </div>
            </div>
            <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {/* Quick Actions */}
        <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
          <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
            Quick Actions
          </h3>
          <div className="space-y-2">
            <Link
              to="/resources"
              className="flex w-full items-center justify-between rounded-lg bg-slate-50 p-4 transition-colors hover:bg-slate-100"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                  <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div className="text-left">
                  <span className="font-medium text-slate-800">Resources</span>
                  <p className="text-xs text-slate-500">Training materials & guides</p>
                </div>
              </div>
              <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link
              to="/routes"
              className="flex w-full items-center justify-between rounded-lg bg-slate-50 p-4 transition-colors hover:bg-slate-100"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                  <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                </div>
                <div className="text-left">
                  <span className="font-medium text-slate-800">My Calls</span>
                  <p className="text-xs text-slate-500">Track your monthly visit progress</p>
                </div>
              </div>
              <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link
              to="/sets"
              className="flex w-full items-center justify-between rounded-lg bg-slate-50 p-4 transition-colors hover:bg-slate-100"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                  <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <div className="text-left">
                  <span className="font-medium text-slate-800">My Sets</span>
                  <p className="text-xs text-slate-500">View scheduled display installations</p>
                </div>
              </div>
              <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link
              to="/scan-upc"
              className="flex w-full items-center justify-between rounded-lg bg-slate-50 p-4 transition-colors hover:bg-slate-100"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100">
                  <svg className="h-5 w-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                </div>
                <div className="text-left">
                  <span className="font-medium text-slate-800">Scan UPC</span>
                  <p className="text-xs text-slate-500">Look up products by barcode</p>
                </div>
              </div>
              <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link
              to="/diagnostics"
              className="flex w-full items-center justify-between rounded-lg bg-slate-50 p-4 transition-colors hover:bg-slate-100"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
                  <svg className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                  </svg>
                </div>
                <div className="text-left">
                  <span className="font-medium text-slate-800">Diagnostics</span>
                  <p className="text-xs text-slate-500">Scanner, GPS, or send a device report</p>
                </div>
              </div>
              <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Admin: Sync Engine Diagnostics (only visible to superusers) */}
        {canImpersonate && (
          <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
            <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
              Admin: Developer Tools
            </h3>
            <Link
              to="/debug/sync"
              className="flex w-full items-center justify-between rounded-lg bg-slate-50 p-4 transition-colors hover:bg-slate-100"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                  <svg className="h-5 w-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div className="text-left">
                  <span className="font-medium text-slate-800">Sync Engine Diagnostics</span>
                  <p className="text-xs text-slate-500">Inspect the visit-lifecycle sync queue (#1471)</p>
                </div>
              </div>
              <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        )}

        {/* Admin: Impersonation (only visible to superusers) */}
        {canImpersonate && (
          <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
            <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
              Admin: View as User
            </h3>

            {/* Select user button */}
            <button
              onClick={() => setShowImpersonationList(!showImpersonationList)}
              className="flex w-full items-center justify-between rounded-lg bg-slate-50 p-4 transition-colors hover:bg-slate-100"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
                  <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <span className="font-medium text-slate-800">
                    {currentImpersonation ? 'Switch User' : 'Select User to View'}
                  </span>
                  <p className="text-xs text-slate-500">Test app as different associate</p>
                </div>
              </div>
              <svg
                className={`h-5 w-5 text-slate-400 transition-transform ${showImpersonationList ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Error message */}
            {impersonationError && (
              <div className="mt-2 rounded-lg bg-red-50 p-2 text-xs text-red-600">
                {impersonationError}
              </div>
            )}

            {/* Associate list */}
            {showImpersonationList && (
              <div className="mt-3">
                <div className="relative">
                  <input
                    type="text"
                    value={impersonationSearch}
                    onChange={(e) => setImpersonationSearch(e.target.value)}
                    placeholder="Search by name, email, or associate ID…"
                    className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 pr-9 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    autoFocus
                  />
                  {impersonationSearching && (
                    <svg className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                </div>
                {impersonationSearch.trim() === '' && (
                  <p className="mt-1 text-xs text-slate-500">
                    Showing top 50 most-active reps. Start typing to search everyone.
                  </p>
                )}
              </div>
            )}
            {showImpersonationList && (
              <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200">
                {impersonationAssociates.length === 0 ? (
                  <div className="p-4 text-center text-sm text-slate-500">
                    {impersonationSearch.trim() ? `No associates match "${impersonationSearch.trim()}"` : 'No associates found'}
                  </div>
                ) : (
                  impersonationAssociates.map((assoc) => (
                    <button
                      key={assoc.associateid}
                      onClick={() => handleImpersonate(assoc)}
                      disabled={impersonationLoading || currentImpersonation?.associateid === assoc.associateid}
                      className={`flex w-full items-center justify-between border-b border-slate-100 p-3 text-left transition-colors last:border-0 hover:bg-slate-50 disabled:opacity-50 ${
                        currentImpersonation?.associateid === assoc.associateid ? 'bg-purple-50' : ''
                      }`}
                    >
                      <div>
                        <div className="font-medium text-slate-800">{assoc.name}</div>
                        <div className="text-xs text-slate-500">
                          {assoc.city && assoc.state ? `${assoc.city}, ${assoc.state}` : assoc.email || 'No location'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-medium text-purple-600">
                          {assoc.set_count} sets / {assoc.route_count} calls
                        </div>
                        {currentImpersonation?.associateid === assoc.associateid && (
                          <div className="text-xs text-green-600">Current</div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Reset Session (admin-only, #1873 follow-up — task #67)
            Force-syncs queues, snapshots a diagnostics report, then
            clears UI state. Designed for clean impersonation repros
            and stuck-state recovery without losing real work. */}
        {canImpersonate && (
          <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                  Reset Session (Admin)
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Sync pending work, snapshot diagnostics, then clear cached day / odometer / impersonation state.
                </p>
              </div>
            </div>
            <button
              onClick={handleOpenResetModal}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-100"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset Session
            </button>
          </div>
        )}

        {/* Hard Reset Day (#1964 — admin destructive escape hatch).
            Server voids the rep's open day work, then nukes ALL local
            state. Use ONLY when the rep is stuck in a way that survives
            normal Reset Session (which leaves IDB intact and lets the
            offline-first hydrate reconstitute the stuck state). */}
        {canImpersonate && (
          <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-sm font-medium uppercase tracking-wide text-red-600">
                  Hard Reset Day (Admin)
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Server-side void of the {currentImpersonation ? 'impersonated rep' : "rep"}&apos;s day + total local-state nuke.
                  Forces re-login. Use when Reset Session is not enough (stuck state survives because the
                  PWA re-hydrates open CallLogs from server). Destructive — only for admins.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setHardResetForceFullDay(false);
                setHardResetError(null);
                setHardResetSummary(null);
                setHardResetPhase('idle');
                setShowHardResetModal(true);
              }}
              className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-red-400 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a2 2 0 012-2h2a2 2 0 012 2v3" />
              </svg>
              Hard Reset Day
            </button>
          </div>
        )}

        {/* Hard Reset Day Modal */}
        {showHardResetModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
               onClick={() => { if (hardResetPhase !== 'running') setShowHardResetModal(false); }}>
            <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
                 onClick={(e) => e.stopPropagation()}>
              <h2 className="text-lg font-semibold text-red-700">Hard Reset Day</h2>
              <p className="mt-2 text-sm text-slate-700">
                This will <strong>void</strong>{' '}
                {hardResetForceFullDay ? 'EVERY CallLog (open + closed)' : 'every open CallLog'}{' '}
                for <strong>{currentImpersonation?.name ?? 'this rep'}</strong> on today&apos;s date, then
                wipe all PWA local state and force re-login.
              </p>
              <p className="mt-2 text-xs text-slate-500">
                The server void is required because the offline-first hydrate would otherwise
                reconstitute the stuck state from your existing CallLogs.
              </p>

              <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={hardResetForceFullDay}
                  onChange={(e) => setHardResetForceFullDay(e.target.checked)}
                  disabled={hardResetPhase === 'running'}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">Also void closed CallLogs</span>
                  <span className="block text-xs text-slate-500">
                    Unchecked = only open CallLogs are voided (signed work preserved).
                    Checked = the rep&apos;s entire day is wiped, including completed tasks. Use only
                    when you intend to redo everything.
                  </span>
                </span>
              </label>

              {hardResetError && (
                <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-700">
                  {hardResetError}
                </div>
              )}

              <div className="mt-5 flex gap-3">
                <button
                  onClick={() => setShowHardResetModal(false)}
                  disabled={hardResetPhase === 'running'}
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleHardResetDay}
                  disabled={hardResetPhase === 'running'}
                  className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {hardResetPhase === 'running' ? 'Resetting…' : 'Hard Reset Now'}
                </button>
              </div>

              {hardResetSummary && (
                <p className="mt-3 text-xs text-slate-500">{hardResetSummary}</p>
              )}
            </div>
          </div>
        )}

        {/* Reset Session Modal */}
        {showResetModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
               onClick={() => { if (resetPhase !== 'syncing' && resetPhase !== 'done') setShowResetModal(false); }}>
            <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl"
                 onClick={(e) => e.stopPropagation()}>
              <h2 className="text-lg font-semibold text-slate-800">Reset Session</h2>

              {resetPhase === 'loading' && (
                <p className="mt-3 text-sm text-slate-500">Loading queue counts…</p>
              )}

              {(resetPhase === 'idle' || resetPhase === 'partial') && resetCounts && (
                <>
                  <p className="mt-2 text-sm text-slate-600">
                    Force-sync everything safely, snapshot a diagnostics report, then clear UI state.
                  </p>

                  <div className="mt-3 rounded-lg border border-slate-200 p-3 text-sm">
                    <div className="font-medium text-slate-700 mb-1">Pending work</div>
                    <ul className="space-y-0.5 text-slate-600">
                      <li>
                        <span className="font-mono">{resetCounts.offlineActions}</span> queued offline actions
                        <span className="text-xs text-slate-400"> (drain on next mount)</span>
                      </li>
                      <li>
                        <span className="font-mono">{resetCounts.visitSync}</span> visit sync entries
                      </li>
                      <li>
                        <span className="font-mono">{resetCounts.expenses}</span> queued expenses
                      </li>
                    </ul>

                    <div className="font-medium text-slate-700 mt-3 mb-1">Will be cleared</div>
                    <ul className="space-y-0.5 text-slate-600">
                      <li>dayData{resetCounts.hasActiveDay ? ' — DAY ACTIVE' : ''}</li>
                      <li>odometerData</li>
                      <li>impersonating{resetCounts.impersonating ? ` (currently active)` : ''}</li>
                    </ul>

                    <div className="font-medium text-slate-700 mt-3 mb-1">Preserved</div>
                    <ul className="space-y-0.5 text-slate-600">
                      <li>Auth tokens · associate · product cache · theme</li>
                    </ul>
                  </div>

                  {resetError && (
                    <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{resetError}</div>
                  )}

                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => setShowResetModal(false)}
                      className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    {resetPhase === 'partial' ? (
                      <button
                        onClick={() => handleConfirmReset(true)}
                        className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                      >
                        Clear anyway
                      </button>
                    ) : (
                      <button
                        onClick={() => handleConfirmReset(false)}
                        className="flex-1 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
                      >
                        Sync &amp; Reset
                      </button>
                    )}
                  </div>
                </>
              )}

              {resetPhase === 'syncing' && (
                <div className="mt-3 flex items-center gap-3 text-sm text-slate-600">
                  <svg className="h-5 w-5 animate-spin text-amber-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Syncing queues + sending diagnostics snapshot…
                </div>
              )}

              {resetPhase === 'done' && (
                <div className="mt-3 rounded-lg bg-green-50 p-3 text-sm text-green-800">
                  Reset complete{resetReportId ? ` (diagnostics report #${resetReportId})` : ''}. Reloading…
                </div>
              )}

              {resetPhase === 'error' && (
                <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  {resetError || 'Reset failed'}
                  <button
                    onClick={() => setShowResetModal(false)}
                    className="mt-3 w-full rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Legacy App Link */}
        <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
          <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
            RDM Connect
          </h3>
          <button
            onClick={handleOpenLegacyApp}
            disabled={isLoadingSSO}
            className="flex w-full items-center justify-between rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 p-4 text-white shadow-card transition-all hover:from-primary-700 hover:to-primary-800 active:scale-[0.98] disabled:opacity-70"
          >
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-white/20 p-2">
                {isLoadingSSO ? (
                  <svg className="h-6 w-6 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                )}
              </div>
              <div className="text-left">
                <span className="font-semibold">
                  {isLoadingSSO ? 'Connecting...' : 'Open Full RDM Connect'}
                </span>
                <p className="text-xs text-primary-100">Access documents, reports & admin features</p>
              </div>
            </div>
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Admin Console Link - only for superusers */}
          {canImpersonate && (
            <a
              href={`${BASE_URL}/admin/`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex w-full items-center justify-between rounded-xl bg-slate-700 p-4 text-white shadow-card transition-all hover:bg-slate-800 active:scale-[0.98]"
            >
              <div className="flex items-center space-x-3">
                <div className="rounded-lg bg-white/20 p-2">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <span className="font-semibold">Open Admin Console</span>
                  <p className="text-xs text-slate-300">System administration panel</p>
                </div>
              </div>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          )}
        </div>

        {/* App Settings */}
        <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
          <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
            App Settings
          </h3>
          <div className="space-y-1">
            <div className="flex items-center justify-between rounded-lg p-3 hover:bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                  <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium text-slate-800">Location Services</div>
                  <div className="text-xs text-slate-500">Required for check-in</div>
                </div>
              </div>
              <span className="text-xs font-medium text-green-600">Enabled</span>
            </div>
            {/* Biometric Login Setting */}
            {biometricAvailable && (
              <button
                onClick={handleBiometricToggle}
                disabled={isAuthenticating}
                className="flex w-full items-center justify-between rounded-lg p-3 hover:bg-slate-50 disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                    <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-slate-800">Biometric Login</div>
                    <div className="text-xs text-slate-500">Face ID / Touch ID</div>
                  </div>
                </div>
                <span className={`text-xs font-medium ${biometricEnabled ? 'text-green-600' : 'text-slate-400'}`}>
                  {isAuthenticating ? 'Setting up...' : biometricEnabled ? 'Enabled' : 'Off'}
                </span>
              </button>
            )}

            {/* Biometric message */}
            {(biometricMessage || biometricError) && (
              <div className={`mx-3 mb-2 rounded-lg p-2 text-xs ${biometricError ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                {biometricError || biometricMessage}
              </div>
            )}

            {/* Push Notifications Setting */}
            {notificationsSupported ? (
              <button
                onClick={handleNotificationsToggle}
                disabled={notificationsLoading || notificationPermission === 'denied'}
                className="flex w-full items-center justify-between rounded-lg p-3 hover:bg-slate-50 disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                    <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-slate-800">Notifications</div>
                    <div className="text-xs text-slate-500">
                      {notificationPermission === 'denied'
                        ? 'Blocked in browser settings'
                        : 'Schedule updates & alerts'}
                    </div>
                  </div>
                </div>
                <span className={`text-xs font-medium ${notificationsEnabled ? 'text-green-600' : notificationPermission === 'denied' ? 'text-red-400' : 'text-slate-400'}`}>
                  {notificationsLoading ? 'Setting up...' : notificationsEnabled ? 'Enabled' : notificationPermission === 'denied' ? 'Blocked' : 'Off'}
                </span>
              </button>
            ) : (
              <div className="flex items-center justify-between rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                    <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-slate-800">Notifications</div>
                    <div className="text-xs text-slate-500">Not supported in this browser</div>
                  </div>
                </div>
                <span className="text-xs font-medium text-slate-400">N/A</span>
              </div>
            )}

            {/* Notification message */}
            {(notificationMessage || notificationError) && (
              <div className={`mx-3 mb-2 rounded-lg p-2 text-xs ${notificationError ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                {notificationError || notificationMessage}
              </div>
            )}
            <div className="flex items-center justify-between rounded-lg p-3 hover:bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                  <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium text-slate-800">App Mode</div>
                  <div className="text-xs text-slate-500">
                    {isStandalone ? 'Installed' : 'Browser'}
                  </div>
                </div>
              </div>
              <span className={`text-xs font-medium ${isStandalone ? 'text-green-600' : 'text-slate-400'}`}>
                {isStandalone ? 'PWA' : 'Web'}
              </span>
            </div>

            {/* Install App option - only show when not installed */}
            {!isStandalone && (
              <button
                onClick={handleInstallApp}
                className="flex w-full items-center justify-between rounded-lg p-3 hover:bg-slate-50"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                    <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-slate-800">Install App</div>
                    <div className="text-xs text-slate-500">
                      {isIOS ? 'Add to Home Screen' : 'Install for offline access'}
                    </div>
                  </div>
                </div>
                <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {/* Install message */}
            {installMessage && (
              <div className="mx-3 mb-2 rounded-lg bg-green-50 p-2 text-xs text-green-600">
                {installMessage}
              </div>
            )}
          </div>
        </div>

        {/* About */}
        <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
          <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
            About
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Version</span>
              <span className="font-medium text-slate-800">{__APP_VERSION__}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Build</span>
              <span className="font-medium text-slate-800">{__GIT_COMMIT__}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Branch</span>
              <span className="font-medium text-slate-800">{__GIT_BRANCH__}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Build Date</span>
              <span className="font-medium text-slate-800">
                {(() => {
                  const date = new Date(__BUILD_DATE__);
                  return isNaN(date.getTime()) ? __BUILD_DATE__ || 'N/A' : date.toLocaleDateString();
                })()}
              </span>
            </div>
          </div>
        </div>

        {/* Sign Out */}
        <button
          onClick={logout}
          className="w-full rounded-xl border border-red-200 bg-red-50 py-4 font-semibold text-red-600 transition-colors hover:bg-red-100 active:scale-[0.98]"
        >
          Sign Out
        </button>
      </main>
    </div>
  );
}
